//
import React, { useEffect, useState } from 'react';
import './EditableSymptom.css';
import { CitationItem, SymptomCitations } from '../../../types/Citations';
import BlockingLoader from '../BlockingLoader/BlockingLoader';

interface Props {
  symptom: SymptomCitations;
  onUpdateCitation: (
    symptomId: number,
    updated: CitationItem
  ) => Promise<boolean>;
  onAddCitation: (
    symptomId: number,
    newCitation: CitationItem
  ) => Promise<boolean>;
  onRejectCitation: (
    symptomId: number,
    rejected: CitationItem
  ) => Promise<void>;
}

// TODO this felt implemented haphazardly and probably has performance issues, re-visit
function EditableSymptom({
  symptom,
  onUpdateCitation,
  onAddCitation,
  onRejectCitation,
}: Props): JSX.Element {
  const [citations, setCitations] = useState<CitationItem[]>(symptom.citations);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [draftCitation, setDraftCitation] = useState('');
  const [draftRationale, setDraftRationale] = useState('');
  const [isAdding, setIsAdding] = useState(false);

  // per-item loading overlay (edit/reject)
  const [itemLoadingId, setItemLoadingId] = useState<number | null>(null);
  // add form loading overlay
  const [addLoading, setAddLoading] = useState(false);

  // reject state
  const [rejectingId, setRejectingId] = useState<number | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  // Convert text to title case
  const toTitleCase = (str: string): string => {
    if (!str) return str;
    return str
      .toLowerCase()
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // keep child in sync when parent refreshes citations
  useEffect(() => {
    setCitations(symptom.citations);
  }, [symptom.citations]);

  const startAdd = () => {
    setIsAdding(true);
    setEditingId(null);
    setRejectingId(null);
    setDraftCitation('');
    setDraftRationale('');
  };

  const handleEdit = (c: CitationItem) => {
    setIsAdding(false);
    setRejectingId(null);
    setEditingId(c.citationId);
    setDraftCitation(c.citation);
    setDraftRationale(c.rationale);
  };

  const handleCancel = () => {
    setIsAdding(false);
    setEditingId(null);
    setRejectingId(null);
    setDraftCitation('');
    setDraftRationale('');
    setRejectReason('');
  };

  const handleSubmitExisting = async (c: CitationItem) => {
    const updated: CitationItem = {
      ...c,
      citation: draftCitation,
      rationale: draftRationale,
    };

    setItemLoadingId(c.citationId);
    const succeeded = await onUpdateCitation(symptom.symptomId, updated);
    if (succeeded) {
      setCitations((prev) =>
        prev.map((item) => (item.citationId === c.citationId ? updated : item))
      );
      setEditingId(null);
    } else {
      // optional: surface error UI
      // console.error('Failed to update citation');
    }
    setItemLoadingId(null);
  };

  const handleSubmitNew = async () => {
    const newCitation: CitationItem = {
      citationId: -1,
      citation: draftCitation.trim(),
      rationale: draftRationale.trim(),
      isRejected: false,
      rejectionReason: null,
      dateAdded: null,
      dateUpdated: null,
      addedBy: null,
      updatedBy: null,
    };

    // Show immediately; parent will replace with real id

    setAddLoading(true);
    const succeeded = await onAddCitation(symptom.symptomId, newCitation);
    if (succeeded) {
      setCitations((prev) => [newCitation, ...prev]);
    }

    setAddLoading(false);

    setIsAdding(false);
    setDraftCitation('');
    setDraftRationale('');
  };

  const startReject = (c: CitationItem) => {
    setEditingId(null);
    setIsAdding(false);
    setRejectingId(c.citationId);
    setRejectReason(c.rejectionReason ?? '');
  };

  const submitReject = async (c: CitationItem) => {
    const updated: CitationItem = {
      ...c,
      isRejected: true,
      rejectionReason: rejectReason.trim(),
    };

    // Optimistic UI; parent will remove/refresh as per your rule
    setCitations((prev) =>
      prev.map((item) => (item.citationId === c.citationId ? updated : item))
    );

    setItemLoadingId(c.citationId);
    await onRejectCitation(symptom.symptomId, updated);
    setItemLoadingId(null);

    setRejectingId(null);
    setRejectReason('');
  };

  const cancelReject = () => {
    setRejectingId(null);
    setRejectReason('');
  };

  return (
    <div className="editable-citation-card">
      <div className="editable-symptom-row">
        <div className="editable-symptom-header">{toTitleCase(symptom.symptom)}</div>
        <button
          type="button"
          className="editable-btn editable-symptom-add-btn"
          onClick={startAdd}
        >
          Add citation
        </button>
      </div>

      {citations.length === 0 && !isAdding && (
        <p className="editable-empty">No citations available.</p>
      )}

      {isAdding && (
        <div className="editable-citation-item">
          {addLoading && <BlockingLoader />}
          <label className="editable-label">
            Citation
            <input
              className="editable-input"
              value={draftCitation}
              onChange={(e) => setDraftCitation(e.target.value)}
            />
          </label>
          <label className="editable-label">
            Rationale
            <textarea
              className="editable-textarea"
              value={draftRationale}
              onChange={(e) => setDraftRationale(e.target.value)}
              rows={3}
            />
          </label>
          <div className="editable-actions">
            <button
              type="button"
              className="editable-btn primary"
              onClick={handleSubmitNew}
              disabled={!draftCitation.trim() || addLoading}
            >
              {addLoading ? 'Saving...' : 'Save'}
            </button>
            <button
              type="button"
              className="editable-btn secondary"
              onClick={handleCancel}
              disabled={addLoading}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {citations.map((c) => {
        const isEditing = editingId === c.citationId;
        const isRejecting = rejectingId === c.citationId;

        return (
          <div key={c.citationId} className="editable-citation-item">
            {itemLoadingId === c.citationId && <BlockingLoader />}

            {!isEditing ? (
              <>
                <div className="editable-citation-top">
                  <div>
                    <div className="editable-citation-text">{c.citation}</div>
                    <div className="editable-rationale-text">{c.rationale}</div>
                    {c.isRejected && c.rejectionReason && (
                      <div className="editable-rejected-badge">
                        Rejected: {c.rejectionReason}
                      </div>
                    )}
                  </div>
                  <div className="editable-inline-actions">
                    <button
                      type="button"
                      className="editable-btn"
                      onClick={() => handleEdit(c)}
                      disabled={itemLoadingId === c.citationId}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="editable-btn danger"
                      onClick={() => startReject(c)}
                      disabled={itemLoadingId === c.citationId}
                    >
                      Reject
                    </button>
                  </div>
                </div>

                {isRejecting && (
                  <div className="editable-reject-box">
                    <label className="editable-label">
                      Rejection reason
                      <textarea
                        className="editable-textarea"
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                        rows={2}
                      />
                    </label>
                    <div className="editable-actions">
                      <button
                        type="button"
                        className="editable-btn primary"
                        onClick={() => submitReject(c)}
                        disabled={
                          !rejectReason.trim() || itemLoadingId === c.citationId
                        }
                      >
                        Submit
                      </button>
                      <button
                        type="button"
                        className="editable-btn secondary"
                        onClick={cancelReject}
                        disabled={itemLoadingId === c.citationId}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <>
                <label className="editable-label">
                  Citation
                  <input
                    className="editable-input"
                    value={draftCitation}
                    onChange={(e) => setDraftCitation(e.target.value)}
                  />
                </label>
                <label className="editable-label">
                  Rationale
                  <textarea
                    className="editable-textarea"
                    value={draftRationale}
                    onChange={(e) => setDraftRationale(e.target.value)}
                    rows={3}
                  />
                </label>
                <div className="editable-actions">
                  <button
                    type="button"
                    className="editable-btn primary"
                    onClick={() => handleSubmitExisting(c)}
                    disabled={itemLoadingId === c.citationId}
                  >
                    {itemLoadingId === c.citationId ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    type="button"
                    className="editable-btn secondary"
                    onClick={handleCancel}
                    disabled={itemLoadingId === c.citationId}
                  >
                    Cancel
                  </button>
                </div>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default EditableSymptom;
